import { lazy } from "react";
import { Navigate, Route } from "react-router";
import { all_routes } from "./all_routes";
import supportingGuides from "../supporting-guides/supportingGuides.json";
import visaChecklist from "../visa-checklist/visaChecklist.json";
import blockedAccountCalculator from "../blocked-account-calculator/blockedAccountCalculator.json";

// Pages load on demand (own JS chunk), so visitors don't download the whole app first
const HomeFour = lazy(() => import("../HomePages/home-four/homeFour"));
const Courses = lazy(() => import("../Courses/courses"));
const Testimonials = lazy(() => import("../Pages/testimonials/testimonials"));
const Faq = lazy(() => import("../Pages/faq/faq"));
const CourseDetailsWrapper = lazy(() => import("../Courses/course-details/CourseDetailsWrapper"));
const BookAppointment = lazy(() => import("../Pages/BookAppointment"));
const FaqG2G = lazy(() => import("../Pages/faq/FaqG2G"));
const ContactUsG2G = lazy(() => import("../Pages/contact-us/ContactUsG2G"));
const Blog = lazy(() => import("../blog/Blog"));
const Checkout = lazy(() => import("../Courses/course-checkout/Checkout"));
const AboutUsG2G = lazy(() => import("../Pages/about-us/aboutUsG2G"));
const LoginPage = lazy(() => import("../auth/login/LoginPage"));
const RegisterPage = lazy(() => import("../auth/register/RegisterPage"));
const EligibilityCheckPage = lazy(() => import("../Pages/eligibility-check/EligibilityCheckPage"));
const AusbildungSectorsPage = lazy(() => import("../ausbildung/AusbildungSectorsPage"));
const VolunteerCircularPage = lazy(() => import("../volunteer/VolunteerCircularPage"));
const ApplicationCheckPage = lazy(() => import("../application-check/ApplicationCheckPage"));
const VisaWaitingTimeTrackerPage = lazy(() => import("../visa-tracker/VisaWaitingTimeTrackerPage"));
const SalaryCalculatorPage = lazy(() => import("../salary-calculator/SalaryCalculatorPage"));
const VisaDocumentChecklistPage = lazy(() => import("../visa-checklist/VisaDocumentChecklistPage"));
const BlockedAccountCalculatorPage = lazy(() => import("../blocked-account-calculator/BlockedAccountCalculatorPage"));
const AusbildungFromBangladeshPage = lazy(() => import("../ausbildung-bangladesh/AusbildungFromBangladeshPage"));
const FsjBfdFromBangladeshPage = lazy(() => import("../fsj-bfd-bangladesh/FsjBfdFromBangladeshPage"));
const StudyFromBangladeshPage = lazy(() => import("../study-bangladesh/StudyFromBangladeshPage"));
const SupportingGuidePage = lazy(() => import("../supporting-guides/SupportingGuidePage"));
const GermanInBangladeshPage = lazy(() => import("../german-bangladesh/GermanInBangladeshPage"));
const AusbildungSectorPage = lazy(() => import("../ausbildung/AusbildungSectorPage"));
const AusbildungProfessionPage = lazy(() => import("../ausbildung/AusbildungProfessionPage"));
const AusbildungFavouritesPage = lazy(() => import("../ausbildung/AusbildungFavouritesPage"));
const DocumentTranslationPage = lazy(() => import("../document-translation/DocumentTranslationPage"));
const CustomerRegisterPage = lazy(() => import("../document-translation/CustomerRegisterPage"));
const FreeToolsHubPage = lazy(() => import("../free-tools/FreeToolsHubPage"));

const routes = all_routes;

export const publicRoutes = [
  // Supporting SEO pages (/study-in-germany-without-ielts …), one shared page reading supportingGuides.json
  ...supportingGuides.guides.map((guide) => ({ path: guide.path, element: <SupportingGuidePage />, route: Route })),
  // German visa checklists (/german-student-visa-checklist …), one shared page reading visaChecklist.json
  ...visaChecklist.routes.map((checklistRoute) => ({ path: checklistRoute.path, element: <VisaDocumentChecklistPage />, route: Route })),
  // Blocked account calculators (/blocked-account-calculator-student …), one shared page reading blockedAccountCalculator.json
  ...blockedAccountCalculator.routes.map((calculatorRoute) => ({ path: calculatorRoute.path, element: <BlockedAccountCalculatorPage />, route: Route })),
  {
    path: "/",
    name: "Root",
    element: <Navigate to="/index" />,
    route: Route,
  },
  // Certified translation for everyone (public customers get an account under /account)
  { path: routes.documentTranslation, element: <DocumentTranslationPage />, route: Route },
  { path: routes.documentTranslationRegister, element: <CustomerRegisterPage />, route: Route },
  // Every free tool and guide on one page — the link other pages point to
  { path: routes.freeToolsHub, element: <FreeToolsHubPage />, route: Route },
  {
    path: routes.homefour,
    element: <HomeFour />,
    route: Route,
  },

  {
    path: routes.courseCheckout,
    element: <Checkout />,
    route: Route,
  },

  {
    path: routes.courseDetails,
    element: <CourseDetailsWrapper />, // this will fetch course based on slug
    route: Route,
  },
  {
    path: routes.courses,
    element: <Courses />,
    route: Route,
  },
  {
    path: routes.bookAppointment,
    element: <BookAppointment />,
    route: Route,
  },
  {
    path: routes.faq,
    element: <FaqG2G />,
    route: Route,
  },
  {
    path: routes.blog,
    element: <Blog />,
    route: Route,
  },
  {
    path: routes.about_us,
    element: <AboutUsG2G />,
    route: Route,
  },
  {
    path: routes.contactUs,
    element: <ContactUsG2G />,
    route: Route,
  },
  {
    path: routes.testimonials,
    element: <Testimonials />,
    route: Route,
  },
  {
    path: routes.FAQ,
    element: <Faq />,
    route: Route,
  },

  {
    path: routes.eligibilitycheck,
    element: <EligibilityCheckPage />,
    route: Route,
  },
  {
    path: routes.ausbildungFinder,
    element: <AusbildungSectorsPage />,
    route: Route,
  },
  {
    path: routes.fsjBfdCircular,
    element: <VolunteerCircularPage audience="public" />,
    route: Route,
  },
  {
    path: routes.cvMotivationLetterCheck,
    element: <ApplicationCheckPage />,
    route: Route,
  },
  {
    path: routes.visaWaitingTimeTracker,
    element: <VisaWaitingTimeTrackerPage />,
    route: Route,
  },
  {
    path: routes.salaryCalculator,
    element: <SalaryCalculatorPage />,
    route: Route,
  },
  {
    path: routes.ausbildungFromBangladesh,
    element: <AusbildungFromBangladeshPage />,
    route: Route,
  },
  {
    path: routes.fsjBfdFromBangladesh,
    element: <FsjBfdFromBangladeshPage />,
    route: Route,
  },
  {
    path: routes.studyFromBangladesh,
    element: <StudyFromBangladeshPage />,
    route: Route,
  },
  {
    path: routes.germanInBangladesh,
    element: <GermanInBangladeshPage />,
    route: Route,
  },
  {
    path: routes.ausbildungFavourites,
    element: <AusbildungFavouritesPage />,
    route: Route,
  },
  {
    path: routes.ausbildungSector,
    element: <AusbildungSectorPage />,
    route: Route,
  },
  {
    path: routes.ausbildungProfession,
    element: <AusbildungProfessionPage />,
    route: Route,
  },
];

// export const authRoutes = [
//   {
//     path: routes.register,
//     element: <RegisterPage />,
//     route: Route,
//   },
// ];
